/**
 * BestOptionFinder — Reusable widget for finding the best vendor for a repair
 * Used in: RepairDetail sidebar + New Repair modal
 */
import { useState } from 'react';
import api from '@/api';
import { Search, Star, Clock, DollarSign, Loader2, Trophy, Zap, CheckCircle2, ChevronDown } from 'lucide-react';
import { CATEGORY_LIST } from '@/constants/repairOptions';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const fmt = (n) => n != null ? new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(n) : '—';

function RankCard({ rank, result, icon: Icon, color, label, onAssign, assignLabel }) {
  if (!result) return null;
  const v = result.vendor;
  return (
    <div className={`p-4 rounded-sm border ${color.bg} space-y-2`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} className={color.text} />
          <span className={`text-[9px] font-bold uppercase tracking-widest font-['JetBrains_Mono'] ${color.text}`}>{label}</span>
        </div>
        <span className={`text-lg font-['JetBrains_Mono'] font-bold ${color.text}`}>{fmt(result.estimated_total)}</span>
      </div>
      <div>
        <p className="text-white font-['Manrope'] font-bold text-sm">{v.name}</p>
        <p className="text-[#5C6A7A] text-xs font-['IBM_Plex_Sans']">{v.city}, {v.us_state}</p>
        {result.best_price_item && (
          <p className="text-[#B8C0CC] text-xs font-['IBM_Plex_Sans'] mt-0.5">
            {result.best_price_item.description} — {fmt(result.best_price_item.unit_price)}/{result.best_price_item.unit}
          </p>
        )}
      </div>
      <div className="flex items-center gap-3 text-[10px] font-['JetBrains_Mono']">
        {v.ops_rating > 0 && (
          <span className="flex items-center gap-1 text-[#3B82F6]"><Star size={10} /> {v.ops_rating}</span>
        )}
        {v.eta_hours && (
          <span className="flex items-center gap-1 text-[#5C6A7A]"><Clock size={10} /> {v.eta_hours}h ETA</span>
        )}
        {result.travel_fee > 0 && (
          <span className="text-[#5C6A7A]">+{fmt(result.travel_fee)} travel</span>
        )}
      </div>
      {v.contract_notes && (
        <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] italic border-t border-[#2D3F5E]/50 pt-2">{v.contract_notes}</p>
      )}
      {onAssign && (
        <button onClick={() => onAssign(result)} className={`w-full py-1.5 text-xs font-bold uppercase tracking-wide font-['JetBrains_Mono'] rounded-sm border transition-all ${color.btn}`}>
          {assignLabel || 'Assign This Vendor'}
        </button>
      )}
    </div>
  );
}

export default function BestOptionFinder({ repairId, onAssign, defaultCategory, defaultState, compact = false }) {
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [form, setForm] = useState({
    state: defaultState || '',
    city: '',
    category: defaultCategory || '',
    service_code: '',
    qty: 1,
  });

  const search = async (e) => {
    e?.preventDefault();
    setSearching(true);
    setResults(null);
    try {
      const res = await api.post('/vendor-pricing/search', form);
      setResults(res.data);
    } catch { }
    finally { setSearching(false); }
  };

  const handleAssign = async (result) => {
    if (onAssign) {
      onAssign(result.vendor.name, result.vendor.id);
    }
  };

  return (
    <div className="space-y-3" data-testid="best-option-finder">
      {!compact && (
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">// BEST VENDOR FINDER</p>
      )}

      {/* Search form */}
      <form onSubmit={search} className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">State</label>
            <select value={form.state} onChange={e => setForm(p=>({...p,state:e.target.value}))}
              className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
              <option value="">Any state</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">Category</label>
            <select value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value}))}
              className="w-full h-8 px-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]">
              <option value="">All categories</option>
              {CATEGORY_LIST.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" disabled={searching}
          className="w-full h-8 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-xs uppercase tracking-wide rounded-sm hover:bg-[#2563EB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {searching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          {searching ? 'Searching...' : 'Find Best Vendor'}
        </button>
      </form>

      {/* Results */}
      {results && (
        <div className="space-y-2">
          {results.all?.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No vendors found with pricing for this category/location.</p>
              <p className="text-[10px] text-[#2D3F5E] font-['IBM_Plex_Sans'] mt-1">Add vendors and their pricing in the Vendor Directory.</p>
            </div>
          ) : (
            <>
              <RankCard rank={1} result={results.best_overall} icon={Trophy}
                color={{ bg:'bg-[#3B82F6]/8 border-[#3B82F6]/30', text:'text-[#3B82F6]', btn:'text-[#3B82F6] border-[#3B82F6]/40 hover:bg-[#3B82F6]/10' }}
                label="Best Overall" onAssign={onAssign ? handleAssign : null} />
              <RankCard rank={2} result={results.cheapest} icon={DollarSign}
                color={{ bg:'bg-[#57EB31]/5 border-[#57EB31]/20', text:'text-[#57EB31]', btn:'text-[#57EB31] border-[#57EB31]/40 hover:bg-[#57EB31]/10' }}
                label="Cheapest" onAssign={onAssign ? handleAssign : null} />
              <RankCard rank={3} result={results.fastest} icon={Zap}
                color={{ bg:'bg-blue-900/10 border-blue-800/30', text:'text-blue-400', btn:'text-blue-400 border-blue-800/40 hover:bg-blue-900/20' }}
                label="Fastest ETA" onAssign={onAssign ? handleAssign : null} />

              {/* Show all */}
              {results.all?.length > 3 && (
                <button onClick={() => setShowAll(!showAll)} className="w-full text-[10px] text-[#5C6A7A] hover:text-[#3B82F6] font-['JetBrains_Mono'] transition-colors flex items-center justify-center gap-1 py-1">
                  <ChevronDown size={12} className={showAll ? 'rotate-180' : ''} />
                  {showAll ? 'Hide' : `Show all ${results.all.length} vendors`}
                </button>
              )}
              {showAll && results.all?.slice(3).map((r, i) => (
                <div key={r.vendor.id} className="flex items-center justify-between p-3 card-industrial">
                  <div>
                    <p className="text-sm text-white font-['Manrope'] font-bold">{r.vendor.name}</p>
                    <p className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans']">{r.vendor.city}, {r.vendor.us_state} · ETA: {r.vendor.eta_hours}h</p>
                  </div>
                  <div className="text-right">
                    <p className="font-['JetBrains_Mono'] font-bold text-[#E8EAED]">{fmt(r.estimated_total)}</p>
                    {onAssign && (
                      <button onClick={() => handleAssign(r)} className="text-[10px] text-[#3B82F6] hover:underline font-['JetBrains_Mono']">Assign</button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
